import { useContext } from 'react'

import {
  digObject,
  launchModal,
  mergeAndStringifyEntityObjects,
} from '@domain-group/javascript-utils'

import { useForm, useLatestEntity } from '@domain-group/react-hooks'
import type { UseFormOptions } from '@domain-group/react-hooks'

import useDispatch from '@hooks/useDispatch'
import useSelector from '@hooks/useSelector'

import PageContext from '@contexts/pageContext'

import * as portalOptionActions from '@redux/modules/portalOption'
import type { AppDispatch } from '@redux/store'
import type { DeleteParams } from '@redux/modules/types'

import defaultFormState, { requiredFields } from '@models/portalOption'
import type { PortalOptionModel, PortalOptionRequestOptions } from '@models/types'

type CreatePortalOptionParams = {
  dispatch: AppDispatch,
  portalOptionParams: Partial<PortalOptionModel>,
  requestOptions?: PortalOptionRequestOptions,
}

const createPortalOption = (params: CreatePortalOptionParams) => {
  const { portalOptionParams, dispatch, requestOptions } = params
  const { createPortalOption: createFn } = portalOptionActions

  const updatedParams = mergeAndStringifyEntityObjects({}, portalOptionParams, [], ['node_properties'])

  return dispatch(createFn(updatedParams, requestOptions))
}

type UpdatePortalOptionParams = {
  dispatch: AppDispatch,
  portalOption: PortalOptionModel,
  portalOptionParams: Partial<PortalOptionModel>,
  requestOptions?: PortalOptionRequestOptions,
}

const updatePortalOption = (params: UpdatePortalOptionParams) => {
  const {
    dispatch, portalOption, portalOptionParams, requestOptions,
  } = params
  const { updatePortalOption: updateFn } = portalOptionActions

  const updatedParams = mergeAndStringifyEntityObjects(portalOption, portalOptionParams, [], ['node_properties'])

  return dispatch(updateFn(updatedParams, requestOptions))
}

type DeletePortalOptionParams = {
  portalOption: DeleteParams<PortalOptionModel>,
  dispatch: AppDispatch,
}

const deletePortalOption = (params: DeletePortalOptionParams) => {
  const { portalOption, dispatch } = params
  const { deletePortalOption: deleteFn } = portalOptionActions

  return dispatch(deleteFn(portalOption))
}

type CustomFormOptions = {
  customRequiredFields?: UseFormOptions['requiredFields'],
}

export function usePortalOptionForm(
  portalOption: Partial<PortalOptionModel>,
  options: UseFormOptions & CustomFormOptions = {},
) {
  const { customRequiredFields = [], validateOn } = options || {}

  const portalOptionForm = useForm(
    defaultFormState,
    { entity: portalOption, requiredFields: [...requiredFields, ...customRequiredFields], validateOn },
    [portalOption.id, portalOption.cache_key],
  )

  return {
    ...portalOptionForm,
  }
}

function usePortalOption(initEntity: Partial<PortalOptionModel> = {}) {
  // @ts-expect-error TS2322
  const { entity: portalOption }: { entity: PortalOptionModel } = useLatestEntity(initEntity, 'portalOptions')

  const dispatch = useDispatch()

  const { callbacks } = useContext(PageContext)

  const { creating, deleting, updating } = useSelector(reduxState => reduxState.portalOptions)

  const nodeProperties = digObject(portalOption, 'node_properties', [])

  return {
    callbacks: {
      createOrEditPortalOption: () => launchModal({
        callbacks,
        modalKey: 'CreateOrEditPortalOptionModal',
        payload: { portalOption },
      }),
      createPortalOption: (
        portalOptionParams: Partial<PortalOptionModel>,
        requestOptions?: PortalOptionRequestOptions,
      ) => (
        createPortalOption({ portalOptionParams, dispatch, requestOptions })
      ),
      deletePortalOption: () => deletePortalOption({ portalOption, dispatch }),
      updatePortalOption: (portalOptionParams: PortalOptionModel, requestOptions?: PortalOptionRequestOptions) => (
        updatePortalOption({
          portalOption, portalOptionParams, dispatch, requestOptions,
        })
      ),
    },
    creating,
    deleting,
    hasNodeProperties: !!nodeProperties.length,
    portalOption,
    updating,
  }
}

export default usePortalOption
